// Pure, dependency-free paint values for the flyover's route line and head marker, shared by the live
// web preview (TrailPreview.ts) and the headless share-video renderer (lib/flyover_renderer/entry.ts).
//
// Both apps draw every route as a coloured line with a HALF-BRIGHTNESS border of the same hue
// (iOS `PolylineAnnotation.setBorderColor`, Android `addPolyline`'s `darkerColor` — both literally
// rgb * 0.5 with a 2px border on a 6px line). The flyover used a flat 5px line with no border, which
// dissolves into satellite imagery. Same recipe here, so a shared video reads like the map in the app.
//
// The video is a further problem of scale: a 9x16 frame is 1080 DEVICE px — a phone screen's worth of
// pixels rendered into a 1080 CSS-px viewport — so map furniture sized for a browser preview comes out
// several times too small in the clip. `flyoverVideoStyle` states the video's sizes in FRAME px, and
// `flyoverFrameScale` stretches them for a wider frame.

/** The apps' border colour: the line's own colour at half brightness. */
export const BORDER_DARKEN_FACTOR = 0.5;

/** Apps use a 2px border on a 6px line; keeping the ratio lets one `scale` drive both. */
export const LINE_BORDER_RATIO = 1 / 3;

/** Live web preview defaults (CSS px) — the video multiplies these by `scale`. */
export const DEFAULT_LINE_COLOR = "#2cc2e3"; // HiikerColor.blue
export const DEFAULT_LINE_WIDTH = 5;
export const DEFAULT_HEAD_COLOR = "#038AA8";
export const DEFAULT_HEAD_RING_COLOR = "#ffffff";
export const DEFAULT_HEAD_RADIUS = 8;
export const DEFAULT_HEAD_RING_WIDTH = 3;

/** Frame width the video's px sizes are authored against (the 9x16 render). */
export const VIDEO_BASE_WIDTH = 1080;
/** Same cap the overlay uses for text, so a 16x9 frame doesn't get proportionally fatter. */
export const VIDEO_SCALE_CAP = 1.55;

/**
 * The video's own sizes, in px of a 1080-wide frame (`flyoverFrameScale` handles wider ones). Red is
 * the route colour both apps ship (`TrailLine.main` / `RouteStyleType.MAIN`), so the clip matches the
 * line a viewer sees on the map; the head puck keeps its teal so it reads against the route.
 */
export const VIDEO_LINE_COLOR = "#DB2B35";
export const VIDEO_LINE_WIDTH = 12;
export const VIDEO_HEAD_RADIUS = 17;
export const VIDEO_HEAD_RING_WIDTH = 6;

/** Waypoint pin + label sizes, same frame-px basis as the route line above. */
export const VIDEO_WAYPOINT_RADIUS = 18;
export const VIDEO_WAYPOINT_RING_WIDTH = 9;
export const VIDEO_WAYPOINT_LABEL_SIZE = 48;
export const VIDEO_WAYPOINT_LABEL_HALO = 5.4;

export interface FlyoverLineStyleOptions {
  /** Route line colour. Its half-brightness shade becomes the border unless `borderColor` is given. */
  color?: string;
  /** Route line width in px at scale 1. */
  width?: number;
  /** Explicit border colour; defaults to `color` darkened, as the apps do. */
  borderColor?: string;
  /** Explicit border width in px at scale 1; defaults to a third of the line width. */
  borderWidth?: number;
  /** Head marker fill / ring colours. */
  headColor?: string;
  headRing?: string;
  /** Explicit colour for the puck's outer ring; defaults to `headColor` darkened. */
  headBorderColor?: string;
  /** Head marker radius + white-ring width in px at scale 1. */
  headRadius?: number;
  headRingWidth?: number;
  /** Multiplies every px dimension. 1 = live web preview; see `flyoverVideoStyle` for the video. */
  scale?: number;
  // Legacy `markerOptions` keys kept working for existing callers.
  radius?: number;
  ring?: string;
  ringWidth?: number;
}

export interface FlyoverLineStyle {
  lineColor: string;
  lineWidth: number;
  lineBorderColor: string;
  lineBorderWidth: number;
  /**
   * Width of the casing line drawn UNDER the route (line + a border on each side). GL JS accepts the
   * native SDKs' `line-border-width` paint property but renders nothing for it, so the border has to
   * be a second, wider line layer — same result, and it works on every GL JS build.
   */
  lineCasingWidth: number;
  headColor: string;
  headRing: string;
  headRadius: number;
  headRingWidth: number;
  /**
   * The dark disc drawn UNDER the puck — the marker's counterpart of the line casing. Derived from the
   * puck's own colour, not the line's, so a route in one hue and a puck in another each keep their ring.
   */
  headBorderColor: string;
  headBorderRadius: number;
}

function roundPx(n: number): number {
  return Math.round(n * 100) / 100;
}

function positive(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : fallback;
}

function nonEmpty(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : fallback;
}

/**
 * The apps' border colour: each RGB channel scaled by `factor` (0.5 = half brightness).
 * Accepts #rgb / #rrggbb (with or without the hash); anything unparseable is returned untouched so a
 * caller that hands us `rgb(...)` or a named colour still gets a usable — if unborderd — style.
 */
export function darkenHexColor(hex: string, factor: number = BORDER_DARKEN_FACTOR): string {
  if (typeof hex !== "string") return hex;
  const raw = hex.trim().replace(/^#/, "");
  const full = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return hex;
  const f = Number.isFinite(factor) && factor >= 0 ? factor : BORDER_DARKEN_FACTOR;
  const channel = (i: number) => {
    const v = Math.round(parseInt(full.slice(i * 2, i * 2 + 2), 16) * f);
    return Math.min(255, Math.max(0, v)).toString(16).padStart(2, "0");
  };
  return `#${channel(0)}${channel(1)}${channel(2)}`;
}

/**
 * px multiplier for a rendered frame `viewportWidth` px wide. 1 at 1080 (the width the video's sizes
 * are authored for); wider frames grow with the overlay's own text scale, capped, so 16x9 doesn't come
 * out proportionally chunkier than 9x16.
 */
export function flyoverFrameScale(viewportWidth: number): number {
  const w = positive(viewportWidth, VIDEO_BASE_WIDTH);
  return Math.min(VIDEO_SCALE_CAP, Math.max(1, w / VIDEO_BASE_WIDTH));
}

/** The route line + head marker options the share video renders with, sized for `viewportWidth`. */
export function flyoverVideoStyle(viewportWidth: number): FlyoverLineStyleOptions {
  return {
    color: VIDEO_LINE_COLOR,
    width: VIDEO_LINE_WIDTH,
    headRadius: VIDEO_HEAD_RADIUS,
    headRingWidth: VIDEO_HEAD_RING_WIDTH,
    scale: flyoverFrameScale(viewportWidth)
  };
}

export interface FlyoverWaypointStyle {
  dotRadius: number;
  dotRingWidth: number;
  labelSize: number;
  labelHaloWidth: number;
}

/** Waypoint pin + label sizes for a frame `viewportWidth` px wide (the video's terrain-anchored POIs). */
export function flyoverWaypointStyle(viewportWidth: number): FlyoverWaypointStyle {
  const s = flyoverFrameScale(viewportWidth);
  return {
    dotRadius: roundPx(VIDEO_WAYPOINT_RADIUS * s),
    dotRingWidth: roundPx(VIDEO_WAYPOINT_RING_WIDTH * s),
    labelSize: roundPx(VIDEO_WAYPOINT_LABEL_SIZE * s),
    labelHaloWidth: roundPx(VIDEO_WAYPOINT_LABEL_HALO * s)
  };
}

/** Resolve the flyover's line + head marker paint values from (possibly partial) caller options. */
export function resolveFlyoverLineStyle(options: FlyoverLineStyleOptions = {}): FlyoverLineStyle {
  const o = options || {};
  const scale = positive(o.scale, 1);
  const lineColor = nonEmpty(o.color, DEFAULT_LINE_COLOR);
  const lineWidth = positive(o.width, DEFAULT_LINE_WIDTH) * scale;
  const borderWidth = positive(o.borderWidth, positive(o.width, DEFAULT_LINE_WIDTH) * LINE_BORDER_RATIO) * scale;
  const headRadius = positive(o.headRadius, positive(o.radius, DEFAULT_HEAD_RADIUS)) * scale;
  const headRingWidth = positive(o.headRingWidth, positive(o.ringWidth, DEFAULT_HEAD_RING_WIDTH)) * scale;

  return {
    lineColor,
    lineWidth: roundPx(lineWidth),
    lineBorderColor: nonEmpty(o.borderColor, darkenHexColor(lineColor)),
    lineBorderWidth: roundPx(borderWidth),
    lineCasingWidth: roundPx(lineWidth + borderWidth * 2),
    headColor: nonEmpty(o.headColor, DEFAULT_HEAD_COLOR),
    headRing: nonEmpty(o.headRing, nonEmpty(o.ring, DEFAULT_HEAD_RING_COLOR)),
    headRadius: roundPx(headRadius),
    headRingWidth: roundPx(headRingWidth),
    headBorderColor: nonEmpty(o.headBorderColor, darkenHexColor(nonEmpty(o.headColor, DEFAULT_HEAD_COLOR))),
    headBorderRadius: roundPx(headRadius + headRingWidth + borderWidth)
  };
}
